/**
 * Herbéos · ajout rapide au panier, barre d'achat fixe, message de confirmation.
 * S'appuie sur les événements du thème (@theme/events) pour que le tiroir panier
 * et le compteur du panier se mettent à jour comme avec le formulaire produit.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const ADDED_STATE_MS = 1600;

/* ---------- Message de confirmation ---------- */

let toastEl;
let toastTimer;

function showToast(message) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'hb-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  toastEl.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
}

/* ---------- Ajout rapide ---------- */

async function addToCart(variantId, quantity = 1) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity));
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    body,
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Ajout impossible');
  }
  return data;
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-hb-add]') : null;
  if (!button || button.disabled) return;
  event.preventDefault();

  const variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;

  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  try {
    const data = await addToCart(variantId, Number(button.getAttribute('data-quantity') || 1));

    document.dispatchEvent(
      new CartAddEvent({}, `hb-${variantId}`, {
        source: 'product-form-component',
        itemCount: Number(button.getAttribute('data-quantity') || 1),
        productId: button.getAttribute('data-product-id') || undefined,
        variantId: String(variantId),
        sections: data.sections,
      })
    );

    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), ADDED_STATE_MS);

    const hasDrawer = document.querySelector('cart-drawer-component[auto-open]');
    if (!hasDrawer) {
      showToast(button.getAttribute('data-added-text') || 'Ajouté au panier');
    }
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Ajout impossible');
  } finally {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }
});

/* ---------- Barre d'achat fixe (fiche produit) ---------- */

class HbStickyAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-hb-sticky-submit]');
    this.priceEl = this.querySelector('[data-hb-sticky-price]');
    this.prices = this.#readPrices();
    this.productId = this.dataset.productId;

    this.button?.addEventListener('click', this.#submit);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    requestAnimationFrame(() => this.#observe());
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#submit);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.observer?.disconnect();
  }

  #readPrices() {
    const script = this.querySelector('script[type="application/json"]');
    try {
      return script ? JSON.parse(script.textContent || '{}') : {};
    } catch {
      return {};
    }
  }

  #mainForm() {
    const forms = document.querySelectorAll('product-form-component form[action*="/cart/add"]');
    for (const form of forms) {
      const component = form.closest('product-form-component');
      if (!this.productId || component?.getAttribute('data-product-id') === this.productId) return form;
    }
    return forms[0] || null;
  }

  #observe() {
    const form = this.#mainForm();
    const target = form?.querySelector('[type="submit"]') || form;
    if (!target) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', passed ? 'false' : 'true');
      },
      { threshold: 0 }
    );
    this.observer.observe(target);
  }

  #submit = () => {
    const form = this.#mainForm();
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.querySelector('[type="submit"]')?.click();
    }
  };

  #onVariantUpdate = (event) => {
    const detail = event.detail || {};
    if (this.productId && detail.data?.productId && String(detail.data.productId) !== this.productId) return;
    const variant = detail.resource;
    if (!variant) return;

    if (this.button) this.button.disabled = variant.available === false;
    const price = this.prices[String(variant.id)];
    if (price && this.priceEl) this.priceEl.textContent = price;
  };
}

if (!customElements.get('hb-sticky-atc')) {
  customElements.define('hb-sticky-atc', HbStickyAtc);
}
